import { useDraggable } from '@dnd-kit/core';
import {
  Layout,
  Type,
  Image,
  Video,
  Grid3X3,
  Star,
  Users,
  CreditCard,
  HelpCircle,
  Mail,
  MapPin,
  Megaphone,
  Menu,
  Calendar,
  ShoppingBag,
  Clock,
  Share2,
  Newspaper,
  Minus,
  MoveVertical,
  Code,
} from 'lucide-react';
import { getAllBlocks, getBlocksByCategory } from '@mawkaa/blocks';
import type { BlockCategory } from '@mawkaa/blocks';
import { cn } from '@mawkaa/ui';

const categoryLabels: Record<BlockCategory, string> = {
  layout: 'Mise en page',
  content: 'Contenu',
  media: 'Médias',
  business: 'Business',
  forms: 'Formulaires',
  utility: 'Utilitaires',
};

const blockIcons: Record<string, React.ElementType> = {
  header: Menu,
  hero: Layout,
  text: Type,
  image: Image,
  gallery: Grid3X3,
  video: Video,
  features: Star,
  services: Grid3X3,
  testimonials: Star,
  team: Users,
  pricing: CreditCard,
  faq: HelpCircle,
  contact: Mail,
  map: MapPin,
  cta: Megaphone,
  footer: Layout,
  menu: Menu,
  booking: Calendar,
  products: ShoppingBag,
  hours: Clock,
  social: Share2,
  newsletter: Newspaper,
  divider: Minus,
  spacer: MoveVertical,
  html: Code,
};

interface DraggableBlockProps {
  type: string;
  name: string;
  icon: React.ElementType;
}

function DraggableBlock({ type, name, icon: Icon }: DraggableBlockProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-${type}`,
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={cn(
        'flex items-center gap-2 p-2 rounded-lg cursor-grab hover:bg-gray-100 transition-colors',
        isDragging && 'opacity-50'
      )}
    >
      <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
        <Icon className="w-4 h-4 text-gray-600" />
      </div>
      <span className="text-sm">{name}</span>
    </div>
  );
}

export function BlockPalette() {
  const categories: BlockCategory[] = ['layout', 'content', 'media', 'business', 'forms', 'utility'];

  return (
    <div className="p-4">
      <h3 className="text-sm font-medium text-muted-foreground mb-4">Blocs</h3>
      {categories.map((category) => {
        const blocks = getBlocksByCategory(category);
        if (blocks.length === 0) return null;

        return (
          <div key={category} className="mb-6">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
              {categoryLabels[category]}
            </h4>
            <div className="space-y-1">
              {blocks.map((block) => (
                <DraggableBlock
                  key={block.type}
                  type={block.type}
                  name={block.name}
                  icon={blockIcons[block.type] || Layout}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
